import React from 'react';
import { Alert, Button, Snackbar } from '@mui/material';

/**
 * Notices that the admin has been deployed while this tab stayed open, and reloads it.
 *
 * An operator can keep a tab open for days. After a deploy that tab still runs the old bundle:
 * it asks for chunks that no longer exist and talks to an API that has moved on, and the first
 * sign of trouble is a crash in the middle of someone's work. Support hears "it broke", never
 * "I reloaded and it was fine" — so the tab reloads itself instead of waiting to be told.
 *
 * The one thing it will not do is interrupt typing: while the caret sits in a field the
 * countdown holds, because a reload there throws away a config someone is halfway through
 * writing. It resumes the moment they click away.
 */

/** CRA writes this at build time; its `main.js` entry carries the content hash. */
const MANIFEST_URL = `${process.env.PUBLIC_URL || ''}/asset-manifest.json`;
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const COUNTDOWN_SECONDS = 5;

const readBuildId = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const manifest = await response.json();
    return manifest?.files?.['main.js'] ?? manifest?.entrypoints?.join(',') ?? null;
  } catch {
    // Offline, or a dev server without a manifest — nothing to say either way.
    return null;
  }
};

/** Whether the operator is mid-edit — a text field, a select being typed into, a rich-text box. */
const isTyping = (): boolean => {
  const active = document.activeElement as HTMLElement | null;
  if (!active) return false;
  return (
    active.tagName === 'INPUT' ||
    active.tagName === 'TEXTAREA' ||
    active.isContentEditable === true
  );
};

export const NewVersionWatcher: React.FC = () => {
  const [outdated, setOutdated] = React.useState(false);
  const [secondsLeft, setSecondsLeft] = React.useState(COUNTDOWN_SECONDS);
  const buildId = React.useRef<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      const current = await readBuildId();
      if (cancelled || !current) return;

      // First answer is this tab's own build — anything different after that is a deploy.
      if (buildId.current === null) buildId.current = current;
      else if (current !== buildId.current) setOutdated(true);
    };

    check();
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  // Countdown to the reload, paused while a field has the caret.
  React.useEffect(() => {
    if (!outdated) return;

    const tick = window.setInterval(() => {
      if (isTyping()) return;
      setSecondsLeft((left) => {
        if (left <= 1) {
          window.clearInterval(tick);
          window.location.reload();
          return 0;
        }
        return left - 1;
      });
    }, 1000);

    return () => window.clearInterval(tick);
  }, [outdated]);

  return (
    <Snackbar open={outdated} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert
        severity="info"
        variant="filled"
        action={
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            Reload now
          </Button>
        }
      >
        A new version of the admin is available — reloading in {secondsLeft}s…
      </Alert>
    </Snackbar>
  );
};

export default NewVersionWatcher;
